import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';

import { useSystemPreference } from '../hooks/useSystemPreference';

// Define Spectrum type for CDS v8 (light | dark)
export type Spectrum = 'light' | 'dark';

export type SpectrumPreference = Spectrum | 'system';

export type SpectrumContextValue = {
  spectrum: Spectrum;
  spectrumPreference: SpectrumPreference;
  systemPreference: Spectrum;
  setSpectrumPreference: (spectrum: SpectrumPreference) => void;
};

export const SpectrumContext = createContext<SpectrumContextValue | null>(null);

const ALLOWED_ROOT_DOMAINS = ['coinbase.com', 'coinbase.dev'] as const;

function getCookieDomain() {
  /*
   * Generate a wildcard domain so that the cookie can be read
   * on any coinbase.com domains. Allowing the spectrum to persist
   * across products more reliably.
   */
  const host = window.location.hostname;
  if (host === 'localhost') {
    return host;
  }

  const parts = host.split('.');
  const candidateDomain = parts.slice(-2).join('.');
  if (ALLOWED_ROOT_DOMAINS.includes(candidateDomain as (typeof ALLOWED_ROOT_DOMAINS)[number])) {
    return `.${candidateDomain}`;
  }

  // Fallback to full hostname for unknown domains
  return `.${host}`;
}

function getCookieValue(): SpectrumPreference {
  try {
    const result = Cookies.get('cb_spectrum');
    if (result === 'dark' || result === 'light' || result === 'system') {
      return result;
    }
  } catch (error) {
    // Ignore
  }
  return 'system';
}

function setCookieValue(value: SpectrumPreference) {
  try {
    Cookies.set('cb_spectrum', value, { domain: getCookieDomain() });
  } catch (error) {
    // Ignore
  }
}

function isKnownSpectrum(spectrum: unknown): spectrum is Spectrum {
  return spectrum === 'dark' || spectrum === 'light';
}

function getThemeOverride(): Spectrum | undefined {
  if (isKnownSpectrum(window.webViewOptions?.theme)) {
    return window.webViewOptions?.theme;
  }

  const searchParams = new URLSearchParams(window?.location?.search || '');
  const spectrum = searchParams.get('spectrum');

  if (typeof spectrum === 'string') {
    if (isKnownSpectrum(spectrum)) {
      return spectrum;
    }
  }
  return undefined;
}

type Props = {
  children: React.ReactNode;
};

export function SpectrumProvider({ children }: Props) {
  const systemPreference = useSystemPreference();

  const [spectrumPreference, setSpectrumPreference] = useState<SpectrumPreference>(getCookieValue);

  const spectrum = useMemo(() => {
    const themeOverride = getThemeOverride();
    if (themeOverride) {
      return themeOverride;
    }
    if (spectrumPreference === 'dark' || spectrumPreference === 'light') {
      return spectrumPreference;
    }
    return systemPreference;
  }, [spectrumPreference, systemPreference]);

  useEffect(() => {
    setCookieValue(spectrumPreference);
  }, [spectrumPreference]);

  const value = useMemo(
    () => ({
      setSpectrumPreference,
      spectrum,
      spectrumPreference,
      systemPreference,
    }),
    [setSpectrumPreference, spectrum, spectrumPreference, systemPreference],
  );

  return <SpectrumContext.Provider value={value}>{children}</SpectrumContext.Provider>;
}

export function useSpectrum() {
  const spectrumContext = useContext(SpectrumContext);

  if (!spectrumContext) {
    throw new Error('useSpectrum should be used inside a SpectrumProvider');
  }

  return spectrumContext;
}
